import { CategoryCouponGuide } from "@/components/coupons/CategoryCouponGuide";
import { CouponsHubLink } from "@/components/seo/CouponsHubLink";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useNavigationType, useParams } from "@/lib/router-compat";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SpecialBrandActionsCarousel } from "@/components/landing/SpecialBrandActionsCarousel";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Baby,
  BookOpen,
  Car,
  Cat,
  Coffee,
  Flower2,
  Footprints,
  Gamepad2,
  Gem,
  Glasses,
  Globe,
  GraduationCap,
  Heart,
  Home,
  Landmark,
  Laptop,
  LayoutGrid,
  Package,
  Pill,
  Plane,
  Refrigerator,
  ShieldCheck,
  Shirt,
  ShoppingCart,
  Smartphone,
  Sofa,
  Store,
  Trophy,
  Wrench,
} from "lucide-react";
import { EXTRA_CATEGORY_SUBTITLES } from "@/content/categoryTopicsExtra";

// Ícones por slug têm prioridade sobre o ícone genérico gravado na categoria.
const categorySlugIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  "pets-e-animais": Cat,
  eletrodomesticos: Refrigerator,
  calcados: Footprints,
  "joias-e-acessorios": Gem,
  moveis: Sofa,
  "ferramentas-e-construcao": Wrench,
  "moda-intima": Shirt,
  "infantil-e-bebes": Baby,
  "telefonia-e-internet": Smartphone,
  "oculos-e-otica": Glasses,
  "livros-e-papelaria": BookOpen,
  seguros: ShieldCheck,
  games: Gamepad2,
  automotivo: Car,
  "flores-e-presentes": Flower2,
};
import { useAllBrands, useCategories } from "@/hooks/useBrands";
import type { Brand, Category } from "@/hooks/useBrands";
import { useGlobalStats } from "@/hooks/useGlobalStats";
import { usePageSeo } from "@/hooks/usePageSeo";
import { FeaturedBrandsStrip } from "@/components/marcas/FeaturedBrandsStrip";
import { BrandCard, BrandCardSkeleton } from "@/components/marcas/BrandCard";
import { BreadcrumbJsonLd } from "@/components/seo/JsonLd";
import { useFeaturedBrandsPublic } from "@/hooks/useFeaturedBrands";
import { normalizeBrandName } from "@/lib/brandGrouping";

const SITE_URL = "https://shoppingdobem.com.br";

const categoryDescriptions: Record<string, string> = {
  "alimentos-e-bebidas": "Sabores, bebidas e itens para deixar sua rotina mais prática.",
  beleza: "Cuidados pessoais, cosméticos e fragrâncias para todos os estilos.",
  "casa-decoracao": "Móveis, decoração e utilidades para transformar cada ambiente.",
  educacao: "Cursos, livros e recursos para aprender e desenvolver novas habilidades.",
  eletronicos: "Tecnologia, informática e acessórios para facilitar o seu dia a dia.",
  entretenimento: "Opções de lazer, cultura e diversão para aproveitar bons momentos.",
  esporte: "Equipamentos, roupas e acessórios para diferentes práticas esportivas.",
  "grandes-marcas": "Marcas reconhecidas reunidas em uma seleção de diferentes segmentos.",
  marketplaces: "Grandes vitrines com variedade de produtos em um só lugar.",
  "pets-e-animais": "Produtos e cuidados essenciais para o bem-estar dos seus animais.",
  roupas: "Moda, calçados e acessórios para renovar o guarda-roupa.",
  "saude-e-farmacia": "Medicamentos, bem-estar e cuidados para uma rotina mais saudável.",
  "servicos-e-financas": "Soluções financeiras e serviços úteis para diferentes necessidades.",
  viagens: "Passagens, hospedagens e experiências para planejar sua próxima viagem.",
};

const scrollSlowlyToResults = () => {
  const target = document.getElementById("todas-as-lojas");
  if (!target) return;

  const header = document.querySelector("header");
  const headerHeight = header?.getBoundingClientRect().height ?? 0;
  const targetY = window.scrollY + target.getBoundingClientRect().top - headerHeight;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    window.scrollTo(0, targetY);
    return;
  }

  const startY = window.scrollY;
  const distance = targetY - startY;
  const duration = 1400;
  const startTime = performance.now();

  const animate = (currentTime: number) => {
    const progress = Math.min((currentTime - startTime) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    window.scrollTo(0, startY + distance * eased);
    if (progress < 1) window.requestAnimationFrame(animate);
  };

  window.requestAnimationFrame(animate);
};

const categoryIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  ShoppingCart,
  Package,
  Globe,
  Shirt,
  Plane,
  Laptop,
  Heart,
  Home,
  Pill,
  Coffee,
  Trophy,
  Landmark,
  GraduationCap,
  Gamepad2,
};

const Marcas = () => {
  // `/marcas/:categorySlug` — páginas de categoria indexáveis. Sem parâmetro é
  // a listagem completa (A-Z). A ordenação "Maior doação" continua sendo só
  // estado local, para não criar URL duplicada da mesma listagem.
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [sortOrder, setSortOrder] = useState<"name" | "donation">("name");
  const { data: stats } = useGlobalStats();

  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const { data: featuredBrands } = useFeaturedBrandsPublic();
  const activeCategory = categorySlug
    ? categories?.find((c: Category) => c.slug === categorySlug)
    : undefined;
  const selectedCategory = activeCategory?.id ?? "";

  const { data: brands, isLoading: brandsLoading, error, refetch } = useAllBrands(
    "",
    selectedCategory,
    { enabled: !categorySlug || !!activeCategory, sort: sortOrder },
  );

  const featuredNames = new Set((featuredBrands ?? []).map((brand) => normalizeBrandName(brand.name)));
  const visibleBrands = (brands ?? []).filter((brand) => !featuredNames.has(normalizeBrandName(brand.name)));
  const brandsCount = visibleBrands.length;

  const categoryName = activeCategory?.name;
  const shouldScrollToBrands = Boolean(
    location.state
      && typeof location.state === "object"
      && "scrollToBrands" in location.state
      && location.state.scrollToBrands === true,
  );
  const isInternalCategoryNavigation = navigationType !== "POP" && shouldScrollToBrands;
  const showDiscoverySections = !activeCategory || !categorySlug || isInternalCategoryNavigation;

  useEffect(() => {
    if (!isInternalCategoryNavigation || categoriesLoading || brandsLoading) return;

    const frame = window.requestAnimationFrame(scrollSlowlyToResults);

    return () => window.cancelAnimationFrame(frame);
  }, [location.key, isInternalCategoryNavigation, categoriesLoading, brandsLoading]);

  const selectDonationOrder = () => {
    setSortOrder("donation");
    navigate("/marcas", { state: { preserveScroll: true, scrollToBrands: true } });
  };

  const resultsTitle = sortOrder === "donation" && !categorySlug
    ? "Maior doação"
    : categorySlug && categoryName
      ? `Cupons de desconto de ${categoryName}`
      : categoryName || "Todas as lojas";
  // Em /marcas/{categoria} o topo é omitido, então o título da lista vira o H1 único.
  const ResultsHeading = categorySlug ? "h1" : "h2";
  const resultsDescription = sortOrder === "donation" && !categorySlug
    ? "Lojas organizadas do maior para o menor percentual de doação."
    : categorySlug
      ? categoryDescriptions[categorySlug] || "Encontre lojas parceiras desta categoria."
      : "Todas as lojas parceiras em ordem alfabética, de A a Z.";

  usePageSeo({
    title: activeCategory
      ? activeCategory.seo_title || `Cupons de ${activeCategory.name} | Lojas Parceiras do Shopping do Bem`
      : "Marcas Parceiras | Shopping do Bem",
    description: activeCategory
      ? activeCategory.seo_description || `Cupons, ofertas e descontos nas lojas de ${activeCategory.name}. Compre nas suas lojas favoritas e parte do valor vira doação, sem custo extra.`
      : "Conheça as +300 marcas parceiras do Shopping do Bem. Compre e doe sem custo extra.",
    canonical: categorySlug && activeCategory
      ? `${SITE_URL}/marcas/${activeCategory.slug}`
      : `${SITE_URL}/marcas`,
  });

  return (
    <div className="flex min-h-screen flex-col">
      {activeCategory && !categorySlug && (
        <BreadcrumbJsonLd
          items={[
            { name: "Início", url: SITE_URL },
            { name: "Lojas", url: `${SITE_URL}/marcas` },
            { name: activeCategory.name, url: `${SITE_URL}/marcas/${activeCategory.slug}` },
          ]}
        />
      )}
      <Header />
      <main className="flex-1">
        {/* Introdução + lojas em destaque */}
        {showDiscoverySections && <section className="bg-muted/30">
          <div className="py-12 md:py-16">
            <div className="container">
              <div className="text-center max-w-3xl mx-auto">
                <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                  +{stats?.brandsCount || (brands?.length ?? 0)} Lojas Parceiras
                </h1>
                <p className="text-lg text-muted-foreground">
                  Confira lojas em destaque e gere doações automáticas para a causa que você apoia.
                </p>
              </div>
            </div>
          </div>

          <FeaturedBrandsStrip />
        </section>}

        <SpecialBrandActionsCarousel />

        {/* Categorias */}
        {showDiscoverySections && <section className="border-y bg-background py-12 md:py-16" aria-labelledby="categories-title">
          <div className="container">
            <div className="mb-8 text-center">
              <h2 id="categories-title" className="font-display text-3xl font-bold md:text-4xl">
                Categorias
              </h2>
              <p className="mt-2 text-muted-foreground">
                Navegue pelas categorias e encontre suas lojas favoritas.
              </p>
            </div>

            <nav
              aria-label="Categorias de lojas"
              className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4"
            >
              <Button
                asChild
                className="h-20 justify-center gap-1 px-1.5 text-[11px] sm:gap-2 sm:px-2 sm:text-xs md:h-24 md:gap-3 md:px-4 md:text-base [&_svg]:size-3.5 md:[&_svg]:size-4"
                variant={!categorySlug && sortOrder === "name" ? "default" : "secondary"}
              >
                <Link
                  to="/marcas"
                  state={{ preserveScroll: true, scrollToBrands: true }}
                  onClick={() => setSortOrder("name")}
                >
                  <LayoutGrid className="h-5 w-5" />
                  Todas
                </Link>
              </Button>
              <Button
                className="h-20 justify-center gap-1 px-1.5 text-[11px] sm:gap-2 sm:px-2 sm:text-xs md:h-24 md:gap-3 md:px-4 md:text-base [&_svg]:size-3.5 md:[&_svg]:size-4"
                variant={!categorySlug && sortOrder === "donation" ? "default" : "secondary"}
                onClick={selectDonationOrder}
              >
                <Heart className="h-5 w-5" />
                Maior doação
              </Button>
              {categoriesLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-20 w-full md:h-24" />
                ))
              ) : (
                categories?.map((category: Category) => {
                  const CategoryIcon = category.slug === "pets-e-animais"
                    ? Cat
                    : categoryIconMap[category.icon || "Package"] || Package;
                  return (
                    <Button
                      key={category.id}
                      asChild
                      className="h-20 justify-center gap-1 px-1.5 text-[11px] sm:gap-2 sm:px-2 sm:text-xs md:h-24 md:gap-3 md:px-4 md:text-base [&_svg]:size-3.5 md:[&_svg]:size-4"
                      variant={categorySlug === category.slug ? "default" : "secondary"}
                    >
                      <Link
                        to={`/marcas/${category.slug}`}
                        state={{ preserveScroll: true, scrollToBrands: true }}
                        onClick={() => setSortOrder("name")}
                      >
                        <CategoryIcon className="h-5 w-5" />
                        {category.name}
                      </Link>
                    </Button>
                  );
                })
              )}
            </nav>
          </div>
        </section>}

        {/* Todas as lojas */}
        <section id="todas-as-lojas" className="scroll-mt-20 bg-muted/30 py-12 md:py-16">
          <div className="container">
            <div className="mb-8 text-center">
              <ResultsHeading className="font-display text-3xl font-bold md:text-4xl">
                {resultsTitle}
              </ResultsHeading>
              <p className="mt-2 text-muted-foreground">
                {resultsDescription}
              </p>
              {categorySlug && categoryName && (
                <h2 className="sr-only">Lojas com cupons de {categoryName}</h2>
              )}
            </div>

            {/* Results count */}
            <div className="text-center text-sm text-muted-foreground">
              {brandsLoading ? (
                <Skeleton className="h-4 w-32" />
              ) : (
                <span>{brandsCount} loja{brandsCount !== 1 ? "s" : ""} encontrada{brandsCount !== 1 ? "s" : ""}</span>
              )}
            </div>

            {/* Brands Grid */}
            <div className="mt-6">
              {error ? (
                <div className="text-center py-12">
                  <p className="text-destructive mb-4">Erro ao carregar lojas.</p>
                  <Button onClick={() => refetch()} variant="outline">
                    Tentar novamente
                  </Button>
                </div>
              ) : brandsLoading || (categorySlug && !categories) ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <BrandCardSkeleton key={i} />
                  ))}
                </div>
              ) : visibleBrands.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                  {visibleBrands.map((brand: Brand) => (
                    <BrandCard key={brand.id} brand={brand} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Store className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Nenhuma loja encontrada</h3>
                  <p className="text-muted-foreground mb-4">
                    {categorySlug
                      ? "Não há lojas disponíveis nesta categoria."
                      : "Não encontramos lojas para exibir."}
                  </p>
                  {categorySlug && (
                    <Button asChild variant="outline">
                      <Link to="/marcas">Ver todas as lojas</Link>
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      {activeCategory && categorySlug && <CategoryCouponGuide category={activeCategory} />}
      </main>
      <div className="container mx-auto px-4 py-6 text-center"><CouponsHubLink /></div>
      <Footer />
    </div>
  );
};

export default Marcas;
